import React, { useState } from "react";
import axios from "axios";
import { API, formatApiError } from "@/auth";
import { Copy, Send, Loader2, MessageCircle, Mail, CheckCircle2 } from "lucide-react";

const digits = (s) => (s || "").replace(/[^\d]/g, "");

function buildText(f, ref) {
  const lic = f.license_type === "lhs" ? "Licence LHSunlocker" : `Licence d'un autre logiciel : ${f.software || "?"}`;
  return [
    "Demande de licence LHSunlocker",
    ref ? `Référence : ${ref}` : null,
    `Nom d'utilisateur : ${f.username}`,
    `Email : ${f.email}`,
    `Licence souhaitée : ${lic}`,
    "Durée souhaitée : à confirmer avec l'administrateur",
    f.message ? `Message : ${f.message}` : null,
  ].filter(Boolean).join("\n");
}

export function LicenseOrderForm({ defaults = {} }) {
  const [f, setF] = useState({ username: defaults.username || "", email: defaults.email || "", license_type: "lhs", software: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [copied, setCopied] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const copy = () => {
    navigator.clipboard.writeText(buildText(f, done?.order.reference));
    setCopied(true); setTimeout(() => setCopied(false), 1500);
  };
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr(null);
    try {
      const { data } = await axios.post(`${API}/license-orders`, f);
      setDone(data);
    } catch (er) { setErr(formatApiError(er)); }
    finally { setBusy(false); }
  };

  if (done) return <OrderSent done={done} text={buildText(f, done.order.reference)} onCopy={copy} copied={copied} />;

  return (
    <form onSubmit={submit} className="space-y-3 text-left" data-testid="license-order-form">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Fld label="Nom d'utilisateur"><input required data-testid="order-username" className="input-lhs" value={f.username} onChange={set("username")} /></Fld>
        <Fld label="Adresse e-mail"><input required type="email" data-testid="order-email" className="input-lhs" value={f.email} onChange={set("email")} /></Fld>
      </div>
      <Fld label="Licence souhaitée">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {[["lhs", "Licence LHSunlocker"], ["other", "Licence d'un autre logiciel"]].map(([id, l]) => (
            <button type="button" key={id} data-testid={`order-type-${id}`} onClick={() => setF({ ...f, license_type: id })}
              className={`rounded-lg border py-2.5 text-sm transition-colors ${f.license_type === id ? "border-orange-500 bg-orange-500/10 text-orange-400" : "border-zinc-800 text-zinc-400 hover:text-white"}`}>{l}</button>
          ))}
        </div>
      </Fld>
      {f.license_type === "other" && (
        <Fld label="Nom du logiciel"><input required data-testid="order-software" className="input-lhs" placeholder="ex : UnlockTool, EFT Pro…" value={f.software} onChange={set("software")} /></Fld>
      )}
      <Fld label="Durée souhaitée"><div className="input-lhs text-zinc-500" data-testid="order-duration">À confirmer avec l'administrateur</div></Fld>
      <Fld label="Message complémentaire (facultatif)"><textarea data-testid="order-message" rows={3} className="input-lhs" value={f.message} onChange={set("message")} /></Fld>
      {err && <div className="text-sm text-red-400" data-testid="order-error">{err}</div>}
      <div className="flex flex-wrap gap-2 pt-1">
        <button disabled={busy} data-testid="order-submit" className="btn-orange rounded-lg px-5 py-2.5 text-sm uppercase tracking-wider inline-flex items-center gap-2">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Envoyer ma demande
        </button>
        <button type="button" onClick={copy} data-testid="order-copy" className="rounded-lg px-5 py-2.5 text-sm border border-zinc-700 hover:border-orange-500 inline-flex items-center gap-2 transition-colors">
          <Copy className="w-4 h-4" /> {copied ? "Copié" : "Copier ma demande"}
        </button>
      </div>
    </form>
  );
}

function OrderSent({ done, text, onCopy, copied }) {
  const c = done.contacts || {};
  const msg = encodeURIComponent(text);
  const links = [
    c.admin_whatsapp && { id: "whatsapp", icon: MessageCircle, label: "WhatsApp", v: c.admin_whatsapp, href: `https://wa.me/${digits(c.admin_whatsapp)}?text=${msg}` },
    c.admin_telegram && { id: "telegram", icon: Send, label: "Telegram", v: c.admin_telegram, href: `https://t.me/+${digits(c.admin_telegram)}` },
    c.admin_email && { id: "email", icon: Mail, label: "E-mail", v: c.admin_email, href: `mailto:${c.admin_email}?subject=${encodeURIComponent("Commande " + done.order.reference)}&body=${msg}` },
  ].filter(Boolean);
  return (
    <div className="space-y-4 text-left" data-testid="order-sent">
      <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/5 p-4 text-sm text-emerald-400">
        <CheckCircle2 className="w-4 h-4 inline mr-1" /> Demande enregistrée · référence <span className="font-mono-tech font-bold" data-testid="order-reference">{done.order.reference}</span>
      </div>
      <p className="text-sm text-zinc-400">Contactez maintenant l'administrateur pour connaître le prix et payer :</p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {links.map(l => (
          <a key={l.id} href={l.href} target="_blank" rel="noreferrer" data-testid={`order-contact-${l.id}`}
            className="rounded-xl border border-zinc-800 bg-[#0C0C10] p-4 flex items-center gap-3 hover:border-orange-500 transition-colors">
            <l.icon className="w-5 h-5 text-orange-500 shrink-0" />
            <div className="min-w-0"><div className="text-sm font-semibold">{l.label}</div><div className="text-xs text-zinc-400 font-mono-tech truncate">{l.v}</div></div>
          </a>
        ))}
      </div>
      <button type="button" onClick={onCopy} data-testid="order-copy-after" className="text-xs text-zinc-400 hover:text-orange-400 inline-flex items-center gap-1">
        <Copy className="w-3.5 h-3.5" /> {copied ? "Copié" : "Copier ma demande"}
      </button>
    </div>
  );
}

function Fld({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] uppercase text-zinc-500 mb-1 font-mono-tech">{label}</label>
      {children}
    </div>
  );
}
